import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {AnalyzeParams, AnalysisResult, Insight} from './types'
import {
    aiGatewayUrl,
    proactiveAiAnalysisPromptId,
    proactiveAiAnalyzeErrorType,
    proactiveAiAnalyzeErrorMessage,
    proactiveAiAnalyzeBadJsonErrorType,
    proactiveAiAnalyzeBadJsonErrorMessage,
    promptHubAppId
} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'
import {getPageOutlineAndTargetsForBuilder} from '../aiBuilderGeneration'
import {extractAllData} from './dataExtractor'

const parseAnalysisResponse = (responseText: string): Insight[] => {
    const jsonMatch = responseText.match(/\{[\s\S]*}/)
    if (!jsonMatch) {
        throw new ReportableError({
            errorType: proactiveAiAnalyzeBadJsonErrorType,
            message: proactiveAiAnalyzeBadJsonErrorMessage,
            extras: {responseText}
        })
    }

    const parsed = JSON.parse(jsonMatch[0])

    return (parsed.insights ?? []).map((insight: any, index: number) => ({
        id: insight.id ?? `ins-${index + 1}`,
        componentPointer: insight.componentPointer,
        type: insight.type,
        confidence: insight.confidence ?? 0,
        impact: insight.impact ?? 0.5,
        details: {
            message: insight.details?.message ?? '',
            dataType: insight.details?.dataType,
            innerElementPath: insight.details?.innerElementPath,
            suggestedCssVariable: insight.details?.suggestedCssVariable
        }
    }))
}

export const analyze = async (
    createExtArgs: CreateExtArgs,
    environmentContext: EnvironmentContext,
    params: AnalyzeParams
): Promise<AnalysisResult> => {
    const {serverFacade} = environmentContext
    const {targetPointer, siteContext} = params

    const text = getPageOutlineAndTargetsForBuilder(createExtArgs, targetPointer)
    const {style, fonts, componentLogic} = extractAllData(createExtArgs, targetPointer)

    const requestData = {
        appDefId: promptHubAppId,
        promptId: proactiveAiAnalysisPromptId,
        params: {
            site_context: JSON.stringify(siteContext),
            text: JSON.stringify(text ?? {}),
            style: JSON.stringify(style ?? {}),
            fonts: JSON.stringify(fonts ?? {}),
            component_logic: JSON.stringify(componentLogic ?? {})
        },
        userRequestInfo: {
            additionalTags: 'proactiveAiAnalysis'
        }
    }

    try {
        const response = await serverFacade.post(aiGatewayUrl, requestData)
        if (!response.response.generatedTexts || response.response.generatedTexts.length === 0) {
            throw new ReportableError({
                errorType: proactiveAiAnalyzeBadJsonErrorType,
                message: 'AI response missing generated texts',
                extras: {targetPointer}
            })
        }
        const generatedText = response.response.generatedTexts[0]
        const insights = parseAnalysisResponse(generatedText)
        return {insights}
    } catch (e: any) {
        if (e.errorType === proactiveAiAnalyzeBadJsonErrorType) {
            throw e
        }
        throw new ReportableError({
            errorType: proactiveAiAnalyzeErrorType,
            message: proactiveAiAnalyzeErrorMessage,
            extras: {targetPointer, originalError: e?.message}
        })
    }
}
